import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatTimeAgo, formatNumber, getInitials } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Radio, Video, Users, Loader2, Plus, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LivePage() {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStartLive, setShowStartLive] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [starting, setStarting] = useState(false);

  const fetchStreams = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/live/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStreams(response.data);
    } catch (error) {
      console.error('Failed to fetch streams:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStreams();
    const interval = setInterval(fetchStreams, 10000);
    return () => clearInterval(interval);
  }, [fetchStreams]);

  const handleStartLive = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title for your live');
      return;
    }
    
    setStarting(true);
    try {
      const response = await axios.post(`${API}/live/start`, {
        title: title.trim(),
        description: description.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Live started!');
      setShowStartLive(false);
      setTitle('');
      setDescription('');
      navigate(`/live/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erè lè w ap kòmanse live';
      toast.error(message);
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-20" data-testid="live-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="w-6 h-6 text-primary animate-pulse" />
            <h1 className="text-2xl font-bold">Live</h1>
          </div>
          <Button 
            onClick={() => setShowStartLive(true)}
            className="rounded-full"
            data-testid="start-live-btn"
          >
            <Video className="w-4 h-4 mr-2" />
            Go Live
          </Button>
        </div>
      </header>

      {/* Active Streams */}
      <div className="p-4">
        {streams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Radio className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No live streams active right now</p>
            <p className="text-muted-foreground mt-2">Start your first live stream!</p>
            <Button 
              onClick={() => setShowStartLive(true)}
              className="mt-6 rounded-full"
            >
              <Video className="w-4 h-4 mr-2" />
              Go Live
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {streams.map(stream => (
              <Link
                key={stream.id}
                to={`/live/${stream.id}`}
                className="group relative bg-card rounded-3xl overflow-hidden border border-border/50 hover:border-primary/50 transition-all"
                data-testid={`stream-${stream.id}`}
              >
                {/* Placeholder for video preview */}
                <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <div className="text-center">
                    <Avatar className="w-20 h-20 mx-auto ring-4 ring-primary/30">
                      <AvatarImage src={stream.avatar} />
                      <AvatarFallback className="text-2xl bg-primary/20 text-primary">
                        {getInitials(stream.display_name || stream.username)}
                      </AvatarFallback>
                    </Avatar>
                    <p className="mt-3 font-semibold">{stream.display_name || stream.username}</p>
                  </div>
                </div>
                
                {/* Live badge */}
                <div className="absolute top-3 left-3 flex items-center gap-2 bg-destructive text-destructive-foreground px-3 py-1 rounded-full text-sm font-bold">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  LIVE
                </div>
                
                {/* Viewer count */}
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                  <Eye className="w-4 h-4" />
                  {formatNumber(stream.viewer_count)}
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold line-clamp-1">{stream.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    @{stream.username} · {formatTimeAgo(stream.started_at, language)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Start Live Dialog */}
      <Dialog open={showStartLive} onOpenChange={setShowStartLive}>
        <DialogContent className="sm:max-w-md" data-testid="start-live-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              Start a Live
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Live Title</label>
              <Input
                placeholder="What are you doing today?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="rounded-2xl"
                data-testid="live-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Description (optional)</label>
              <Textarea
                placeholder="Tell us about your live..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="rounded-2xl resize-none"
                rows={3}
              />
            </div>
            
            <Button
              onClick={handleStartLive}
              disabled={starting || !title.trim()}
              className="w-full rounded-full"
              data-testid="confirm-start-live-btn"
            >
              {starting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Radio className="w-4 h-4 mr-2" />
                  Go Live Now
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
